import {
    collection,
    CollectionReference,
    DocumentData,
    Timestamp,
    getCountFromServer,
    doc,
    addDoc,
    deleteDoc,
    getDocs,
    query,
    orderBy,
    where,
    updateDoc,
    getDoc,
    limit
} from "firebase/firestore";

import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { fs, storage } from "../firebase.ts";
import { pickFile } from "../components/global.ts";

export interface Temple {
    id?: string; // doc id
    date: Timestamp; // created
    status: "init" | "deleted" | "inactive" | "active"; // // admin can update
    //TODO: hide deleted from map ??

    aba?: string; // aba bank number // admin can update

    // stats
    rank?: number;
    visitors?: number; // total check-ins
    followers?: string[]; // uids // head monk can alert followers of temple news or book club meetings

    // google place
    pid?: string; // place id // undefined = created by admin // if undefined = admin can delete
    name: string; // displayName // admin can update
    latitude: number;
    longitude: number;
    address: string; // formattedAddress // admin can update
    photo?: string; // src url // admin can update
    link?: string; // google shared link
}

export class Temples {
    private collection: CollectionReference<DocumentData>;

    constructor() {
        this.collection = collection(fs, "temples");
    }

    async count(): Promise<number> {
        const q = query(this.collection, where("status", "==", "active"));
        const snapshot = await getCountFromServer(q);
        return snapshot.data().count;
    }

    async exists(pid: string): Promise<boolean> {
        const q = query(this.collection, where("pid", "==", pid));
        const snapshot = await getDocs(q);
        return !snapshot.empty;
    }

    async add(
        pid: string,
        name: string,
        latitude: number,
        longitude: number,
        address: string
    ): Promise<Temple> {
        const temple: Temple = {
            date: Timestamp.now(),
            status: "init",
            pid,
            name,
            latitude,
            longitude,
            address
        };
        const docRef = await addDoc(this.collection, temple);

        return {
            id: docRef.id,
            ...temple
        };
    }

    async adminAdd(
        name: string,
        latitude: number,
        longitude: number,
        address?: string
    ): Promise<Temple> {
        const temple: Temple = {
            date: Timestamp.now(),
            status: "inactive",
            name,
            latitude,
            longitude,
            address: address || "" // admin can add later
        };
        const docRef = await addDoc(this.collection, temple);

        return {
            id: docRef.id,
            ...temple
        };
    }

    // keep deleted temples for now for archive
    async remove(id: string, pid: string) {
        console.log(pid); // TODO: check if undefined
        if (pid === "undefined") {
            const docRef = doc(fs, "temples", id);
            await updateDoc(docRef, { status: "deleted" });
            // const docRef = doc(fs, "temples", id);
            // await deleteDoc(docRef);
        } else {
            const q = query(this.collection, where("pid", "==", pid), limit(1));
            const snapshot = await getDocs(q);
            const docRef = snapshot.docs[0].ref;
            await updateDoc(docRef, { status: "deleted" });
        }
    }

    async updateStatus(id: string, status: boolean) {
        const docRef = doc(fs, "temples", id);
        await updateDoc(docRef, { status: status ? "active" : "inactive" });
    }

    // TODO: 2 separate calls: later try to update local copy instead to save money
    async updateLink(id: string, link: string): Promise<Temple> {
        const docRef = doc(fs, "temples", id);
        await updateDoc(docRef, { link });
        const temple = await getDoc(docRef);
        return { id: temple.id, ...temple.data() } as Temple;
    }

    // TODO: 2 separate calls: later try to update local copy instead to save money
    async updateName(id: string, name: string): Promise<Temple> {
        const docRef = doc(fs, "temples", id);
        await updateDoc(docRef, { name });
        const temple = await getDoc(docRef);
        return { id: temple.id, ...temple.data() } as Temple;
    }

    // TODO: 2 separate calls: later try to update local copy instead to save money
    async updateAddress(id: string, address: string): Promise<Temple> {
        const docRef = doc(fs, "temples", id);
        await updateDoc(docRef, { address });
        const temple = await getDoc(docRef);
        return { id: temple.id, ...temple.data() } as Temple;
    }

    // upload image bc google map user can remove their photo; also google prevents using their image link
    // TODO: 2 separate calls: later try to update local copy instead to save money
    async updatePhoto(
        id: string,
        progressCallback?: (progress: number) => void,
        useCamera: boolean = false
    ): Promise<Temple | null> {
        const file = await pickFile({
            capture: useCamera ? "camera" : undefined,
            accept: ".jpg,.png,.webp,.jpeg,.svg,.gif"
            // iphone HEIC files don’t work
            // TODO: later allow all types, then convert to smaller
        });

        if (file) {
            try {
                const docRef = doc(fs, "temples", id);
                const temple = await getDoc(docRef);
                if (temple.exists()) {
                    const url = await this.savePhoto(
                        temple.id,
                        file,
                        progressCallback
                    );
                    return {
                        id: temple.id,
                        ...temple.data(),
                        photo: url
                    } as Temple;
                } else return null;
            } catch (error) {
                console.error(error);
                return null;
            }
        } else return null;
    }

    private savePhoto(
        id: string,
        file: File,
        progressCallback?: (progress: number) => void
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            const storageRef = ref(storage, `temples/${id}/photo`); // to replace old one // ${file.name}
            const uploadTask = uploadBytesResumable(storageRef, file, {
                customMetadata: {} //TODO: store profs in metadata?
            });

            // 2. Register the event listener
            uploadTask.on(
                "state_changed",
                // Observer for progress
                (snapshot) => {
                    const progress = Math.round(
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    );
                    //console.log("Upload is " + progress + "% done");

                    // Call the progress callback if it exists
                    if (progressCallback) {
                        progressCallback(progress);
                    }
                },
                // Observer for errors
                (error) => {
                    // Handle unsuccessful uploads and reject the promise
                    //console.error("Upload failed:", error);
                    reject(error);
                },
                // Observer for completion
                async () => {
                    try {
                        // Handle successful uploads on complete
                        //console.log("Upload complete, getting download URL...");
                        const downloadURL = await getDownloadURL(
                            uploadTask.snapshot.ref
                        );
                        const docRef = doc(fs, "temples", id);
                        await updateDoc(docRef, { photo: downloadURL });
                        // Resolve the promise with the download URL
                        resolve(downloadURL);
                    } catch (error) {
                        // If getting the URL fails, reject the promise
                        //console.error("Failed to get download URL:", error);
                        reject(error);
                    }
                }
            );
        });
    }

    async get(status: Temple["status"] | null = null): Promise<Temple[]> {
        const q = status
            ? query(
                  this.collection,
                  where("status", "==", status), // needs index bc status+date
                  orderBy("date", "asc") // or desc
              )
            : query(
                  this.collection,
                  where("status", "!=", "deleted"), // needs index bc status+date
                  orderBy("date", "asc") // or desc
              );
        const results = await getDocs(q);
        const temples: Temple[] = [];

        results.docs.forEach((doc, i) => {
            const temple = doc.data() as Temple;
            temples.push({
                id: doc.id,
                status: temple.status,
                rank: i + 1,
                pid: temple.pid,
                name: temple.name,
                latitude: temple.latitude,
                longitude: temple.longitude,
                address: temple.address,
                photo: temple.photo,
                link: temple.link
            } as Temple);
        });

        return temples;
    }
}
